<template>
  <AModal
    :model-value="visible"
    size="xl"
    @update:model-value="(v) => !v && handleClose()"
  >
    <template #title>
      <div class="atb-modal__title">
        <i class="material-icons-round">app_settings_alt</i>
        <span>앱 교재 설정 — {{ employee?.name || '' }}</span>
      </div>
    </template>
    <template #sub>
      학습앱({{ employee?.email || '-' }})에 노출할 교재 범위를 설정합니다.
    </template>

    <!-- 전체 교재 보기 토글 -->
    <div class="atb-modal__toggle-row">
      <label class="atb-modal__switch">
        <input type="checkbox" v-model="viewAll" />
        <span class="atb-modal__switch-track"><span class="atb-modal__switch-knob"></span></span>
        <span class="atb-modal__switch-label">전체 교재 보기</span>
      </label>
      <span v-if="!viewAll" class="atb-modal__toggle-hint">아래에서 지정한 교재만 앱에 노출됩니다.</span>
    </div>

    <!-- 전체보기 안내 -->
    <div v-if="viewAll" class="atb-modal__notice">
      <i class="material-icons-round">visibility</i>
      <span>승인·승인요청 교재 <b>전체</b>가 앱에 노출됩니다. 아래 지정 목록은 삭제되지 않고 보존됩니다.</span>
    </div>

    <template v-else>
      <!-- 선택 요약 -->
      <div class="atb-modal__summary">
        <div class="atb-modal__summary-head">
          <span class="atb-modal__summary-title">선택된 교재</span>
          <APill :kind="selectedBarcodes.length > 0 ? 'info' : 'warn'">{{ selectedBarcodes.length }}권</APill>
        </div>
        <div v-if="selectedBarcodes.length === 0" class="atb-modal__summary-empty">
          선택된 교재가 없습니다. 이대로 저장하면 앱 교재 목록이 비어 보입니다.
        </div>
        <div v-else class="atb-modal__chips">
          <span v-for="chip in selectedChips" :key="chip.barcode" class="atb-modal__chip" :title="chip.barcode">
            <span class="atb-modal__chip-title">{{ chip.title }}</span>
            <APill v-if="chip.status" :kind="statusPillKind(chip.status)">{{ statusLabel(chip.status) }}</APill>
            <button type="button" class="atb-modal__chip-x" title="선택 해제" @click="removeChip(chip.barcode)">
              <i class="material-icons-round">close</i>
            </button>
          </span>
        </div>
      </div>

      <!-- 교재 검색 -->
      <SearchArea
        class="atb-modal__search"
        :search-fields="searchFields"
        :show-search-button="true"
        :show-download-button="false"
        @search="handleSearch"
      />

      <!-- 교재 목록 -->
      <div class="atb-modal__list-bar">
        <ABtn
          v-if="textbooks.length > 0"
          size="sm"
          kind="ghost"
          :icon="isPageAllSelected ? 'check_box' : 'check_box_outline_blank'"
          @click="togglePageSelectAll"
        >{{ isPageAllSelected ? '이 페이지 해제' : '이 페이지 선택' }}</ABtn>
        <APill kind="info">총 {{ totalCount }}건</APill>
      </div>

      <AEmpty
        v-if="textbooks.length === 0"
        title="교재가 없어요"
        sub="검색 조건을 변경해보세요."
      />

      <ATable
        v-else
        :columns="columns"
        :rows="textbooks"
        row-key="barcode"
      >
        <template #cell:select="{ row }">
          <input type="checkbox" :value="row.barcode" v-model="selectedBarcodes" @change="onRowToggle(row)" />
        </template>
        <template #cell:title="{ row }">
          <div class="atb-modal__book">
            <img
              :src="row.cover_image_url || '/img/default-cover.png'"
              class="atb-modal__book-cover"
              alt="cover"
              @error="onImageError"
            />
            <span class="atb-modal__book-title">{{ row.title }}</span>
          </div>
        </template>
        <template #cell:publisher="{ row }">
          <span class="atb-modal__cell">{{ row.publisher }}</span>
        </template>
        <template #cell:barcode="{ row }">
          <code class="atb-modal__code">{{ row.barcode }}</code>
        </template>
      </ATable>

      <APagination
        v-if="totalCount > 0"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getTextbooks(); }"
      />
    </template>

    <template #foot>
      <ABtn kind="ghost" @click="handleClose">취소</ABtn>
      <ABtn
        kind="primary"
        icon="save"
        :disabled="!isDirty || saving"
        :loading="saving"
        @click="save"
      >저장</ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import SearchArea from "@/components/SearchArea.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";

const STATUS_LABELS = {
  request: "승인요청",
  approval: "승인완료",
  rejection: "거절",
};
const STATUS_PILLS = {
  request: "warn",
  approval: "ok",
  rejection: "err",
};

export default {
  name: "AppTextbookSettingsModal",
  components: { SearchArea, AModal, ABtn, APill, ATable, AEmpty, APagination },
  props: {
    visible: { type: Boolean, default: false },
    employee: { type: Object, default: null },
  },
  emits: ["close"],
  data() {
    return {
      viewAll: false,
      savedViewAll: false,
      selectedBarcodes: [], // 편집 중 선택 (목록 재조회와 무관하게 유지)
      savedBarcodes: [], // 마지막 저장 스냅샷 (dirty 비교용)
      selectedInfoMap: {}, // barcode → { title, publisher, status } 칩 표시용
      textbooks: [],
      totalCount: 0,
      currentPage: 1,
      itemsPerPage: 10,
      searchValues: {},
      saving: false,
      searchFields: [
        { key: "title", placeholder: this.$t("label.14") },
        { key: "publisher", placeholder: this.$t("label.29") },
        { key: "barcode", placeholder: this.$t("label.28") },
      ],
      columns: [
        { key: "select", label: "", align: "center", width: 40 },
        { key: "title", label: this.$t("label.14"), width: 380 },
        { key: "publisher", label: this.$t("label.29"), wrap: true, width: 160 },
        { key: "barcode", label: this.$t("label.28"), width: 150 },
      ],
    };
  },
  computed: {
    isDirty() {
      if (this.viewAll !== this.savedViewAll) return true;
      // 전체보기 저장 시 barcodes는 무시(지정 목록 보존)되므로 선택 변경은 dirty 아님
      if (this.viewAll) return false;
      const a = [...this.selectedBarcodes].sort();
      const b = [...this.savedBarcodes].sort();
      return a.length !== b.length || a.some((v, i) => v !== b[i]);
    },
    selectedChips() {
      return this.selectedBarcodes.map((bc) => ({
        barcode: bc,
        title: this.selectedInfoMap[bc]?.title || bc,
        status: this.selectedInfoMap[bc]?.status || "",
      }));
    },
    isPageAllSelected() {
      return (
        this.textbooks.length > 0 &&
        this.textbooks.every((t) => this.selectedBarcodes.includes(t.barcode))
      );
    },
  },
  watch: {
    visible(v) {
      if (v && this.employee?.idx) this.init();
    },
  },
  methods: {
    statusLabel(status) { return STATUS_LABELS[status] || status; },
    statusPillKind(status) { return STATUS_PILLS[status] || "info"; },
    async init() {
      this.currentPage = 1;
      this.searchValues = {};
      this.textbooks = [];
      this.totalCount = 0;
      await this.fetchSettings();
      await this.getTextbooks();
    },
    async fetchSettings() {
      try {
        const res = await this.$axios({
          method: "get",
          url: `/emp/appTextbooks/${this.employee.idx}`,
          loadingBar: true,
        });
        this.applySettings(res.data);
      } catch (error) {
        console.error("Error :", error);
        this.$notifyVue({ type: "danger", title: "조회 실패", text: "앱 교재 설정을 불러오지 못했습니다." });
        this.$emit("close");
      }
    },
    // GET/PUT 응답 공용 상태 동기화 — 서버가 무시한 barcode는 여기서 자동 정리됨
    applySettings(data) {
      const list = Array.isArray(data?.textbooks) ? data.textbooks : [];
      this.savedViewAll = this.viewAll = !!data?.view_all;
      this.savedBarcodes = list.map((t) => t.barcode);
      this.selectedBarcodes = [...this.savedBarcodes];
      const map = {};
      list.forEach((t) => {
        map[t.barcode] = { title: t.title, publisher: t.publisher, status: t.status };
      });
      this.selectedInfoMap = map;
    },
    async getTextbooks() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/editor/textbooks",
          params: {
            limit: this.itemsPerPage,
            page: this.currentPage,
            search_params: JSON.stringify(this.searchValues),
          },
          loadingBar: true,
        });
        if (res.status === 200) {
          this.textbooks = res.data.items || [];
          this.totalCount = res.data.total_count || 0;
          // 선택된 교재의 칩 정보 보강 (선택 상태는 리셋하지 않음)
          this.textbooks.forEach((row) => {
            if (this.selectedBarcodes.includes(row.barcode)) this.storeRowInfo(row);
          });
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    storeRowInfo(row) {
      const prev = this.selectedInfoMap[row.barcode] || {};
      this.selectedInfoMap = {
        ...this.selectedInfoMap,
        [row.barcode]: {
          title: row.title,
          publisher: row.publisher,
          status: row.status || prev.status,
        },
      };
    },
    onRowToggle(row) {
      if (this.selectedBarcodes.includes(row.barcode)) this.storeRowInfo(row);
    },
    removeChip(barcode) {
      this.selectedBarcodes = this.selectedBarcodes.filter((b) => b !== barcode);
    },
    togglePageSelectAll() {
      const pageBarcodes = this.textbooks.map((t) => t.barcode);
      if (this.isPageAllSelected) {
        this.selectedBarcodes = this.selectedBarcodes.filter((b) => !pageBarcodes.includes(b));
      } else {
        const merged = new Set(this.selectedBarcodes);
        this.textbooks.forEach((row) => {
          merged.add(row.barcode);
          this.storeRowInfo(row);
        });
        this.selectedBarcodes = [...merged];
      }
    },
    handleSearch(values) {
      this.searchValues = values;
      this.currentPage = 1;
      this.getTextbooks();
    },
    async save() {
      if (!this.isDirty || this.saving) return;
      if (!this.viewAll && this.selectedBarcodes.length === 0) {
        const confirm = await swal.fire({
          title: "선택된 교재가 없습니다",
          text: `${this.employee?.name || ""} 님의 앱 교재 목록이 비어 보이게 됩니다. 저장하시겠습니까?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "저장",
          cancelButtonText: "취소",
        });
        if (!confirm.isConfirmed) return;
      }
      this.saving = true;
      try {
        const res = await this.$axios({
          method: "put",
          url: `/emp/appTextbooks/${this.employee.idx}`,
          data: {
            view_all: this.viewAll,
            barcodes: this.selectedBarcodes,
          },
          loadingBar: true,
        });
        this.applySettings(res.data);
        const text = this.viewAll
          ? "전체 교재 보기로 설정되었습니다."
          : `${this.savedBarcodes.length}권이 앱에 노출됩니다.`;
        this.$notifyVue({ type: "success", title: "저장 완료", text });
        this.$emit("close");
      } catch (error) {
        console.error("Error :", error);
        const text = error?.response?.status === 404
          ? "대상 관리자를 찾을 수 없습니다."
          : "앱 교재 설정 저장 중 오류가 발생했습니다.";
        this.$notifyVue({ type: "danger", title: "저장 실패", text });
      } finally {
        this.saving = false;
      }
    },
    async handleClose() {
      if (this.isDirty) {
        const confirm = await swal.fire({
          title: "저장하지 않은 변경이 있습니다",
          text: "닫으면 변경 내용이 사라집니다.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "닫기",
          cancelButtonText: "계속 편집",
        });
        if (!confirm.isConfirmed) return;
      }
      this.$emit("close");
    },
    onImageError(e) {
      e.target.onerror = null;
      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="64"%3E%3Crect fill="%23F0E8D2" width="48" height="64"/%3E%3C/svg%3E';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.atb-modal {
  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;

    > i { font-size: 18px; color: $p-ink-mid; }
  }

  &__toggle-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 14px;
  }

  &__switch {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;

    input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    input:checked + .atb-modal__switch-track {
      background: $p-ok;

      .atb-modal__switch-knob { transform: translateX(16px); }
    }
  }

  &__switch-track {
    width: 38px;
    height: 22px;
    border-radius: 11px;
    background: $p-line;
    padding: 2px;
    transition: background $p-dur $p-ease;
    flex-shrink: 0;
  }

  &__switch-knob {
    display: block;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: $p-shadow-sm;
    transition: transform $p-dur $p-ease;
  }

  &__switch-label {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__toggle-hint {
    font-size: 12px;
    color: $p-ink-soft;
  }

  &__notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-panel-alt;
    font-size: 13px;
    color: $p-ink;

    i { color: $p-ink-mid; font-size: 18px; }
    b { font-weight: 800; }
  }

  &__summary {
    margin-bottom: 14px;
    padding: 12px 14px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-panel-alt;
  }

  &__summary-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__summary-title {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__summary-empty {
    font-size: 12px;
    color: $p-ink-soft;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-height: 120px;
    overflow-y: auto;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px 4px 10px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-panel;
    font-size: 12px;
    max-width: 100%;
  }

  &__chip-title {
    font-weight: 700;
    color: $p-ink;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 240px;
  }

  &__chip-x {
    display: inline-grid;
    place-items: center;
    width: 20px;
    height: 20px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: $p-ink-soft;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 14px; }

    &:hover {
      background: $p-panel-alt;
      color: $p-ink;
    }
  }

  &__search { margin-bottom: 14px; }

  &__list-bar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__book {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__book-cover {
    width: 32px;
    height: 44px;
    object-fit: cover;
    border-radius: $p-radius-sm;
    box-shadow: $p-shadow-sm;
    flex-shrink: 0;
  }

  &__book-title {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 300px;
  }

  &__cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    max-width: 100%;
  }

  &__code {
    display: inline-block;
    padding: 3px 8px;
    background: $p-panel-alt;
    border: 1px solid $p-line;
    border-radius: 5px;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink2;
    font-weight: 700;
  }
}
</style>
